import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LanguageProvider } from "./contexts/LanguageContext";
import { AuthProvider } from "./contexts/AuthContext";
import { AdminLanguageProvider } from "./contexts/AdminLanguageContext";
import HomePage from "./components/HomePage";
import LoginPage from "./admin/LoginPage";
import AdminLayout from "./admin/AdminLayout";
import AcceptInvite from "./admin/AcceptInvite";
import Dashboard from "./admin/Dashboard";
import LeadsPage from "./admin/LeadsPage";
import CustomersPage from "./admin/CustomersPage";
import CustomerDetail from "./admin/CustomerDetail";
import ProjectsPage from "./admin/ProjectsPage";
import InvoicesPage from "./admin/InvoicesPage";
import TicketsPage from "./admin/TicketsPage";
import AgreementsPage from "./admin/AgreementsPage";
import ArticlesPage from "./admin/ArticlesPage";
import PaymentPage from "./components/PaymentPage";
import PaymentSuccess from "./components/PaymentSuccess";
import OnboardingPage from "./components/OnboardingPage";
import CaseDetailPage from "./components/CaseDetailPage";
import SubscriptionPage from "./components/SubscriptionPage";
import AgreementPage from "./components/AgreementPage";
import TimeReportPage from "./components/TimeReportPage";
import CustomerPortalPage from "./components/CustomerPortalPage";
import OrderPage from "./components/OrderPage";
import AcceptAgreement from "./components/AcceptAgreement";
import ProjectPortal from "./components/ProjectPortal";
import OnboardingSessionsPage from "./admin/OnboardingSessionsPage";
import OnboardingDetailPage from "./admin/OnboardingDetailPage";
import BookkeepingPage from "./admin/BookkeepingPage";
import AccountantLayout from "./admin/AccountantLayout";
import OutreachPage from "./admin/OutreachPage";
import PromptsPage from "./admin/PromptsPage";

// Global axios interceptor: attach Bearer token from localStorage
axios.interceptors.request.use((config) => {
  try {
    const token = localStorage.getItem("wedowedo_token");
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch {}
  return config;
});

// Global axios response interceptor: redirect to /admin/login on 401 inside admin area
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    try {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail || "";
      const path = window.location.pathname || "";
      if (status === 401 && path.startsWith("/admin") && !path.endsWith("/admin/login")) {
        localStorage.removeItem("wedowedo_token");
        const reason = /expired/i.test(detail) ? "session_expired" : "auth_required";
        window.location.replace(`/admin/login?reason=${reason}`);
      }
    } catch {}
    return Promise.reject(error);
  }
);

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public landing page */}
        <Route path="/" element={<LanguageProvider><HomePage /></LanguageProvider>} />

        {/* Public payment pages */}
        <Route path="/pay/success" element={<PaymentSuccess />} />
        <Route path="/pay/:invoiceId" element={<PaymentPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/cases/:slug" element={<CaseDetailPage />} />
        <Route path="/subscription/:token" element={<SubscriptionPage />} />
        <Route path="/agreement/:token" element={<AgreementPage />} />
        <Route path="/time-report/:token" element={<TimeReportPage />} />
        <Route path="/portal/:token" element={<CustomerPortalPage />} />
        <Route path="/order/:token" element={<OrderPage />} />
        <Route path="/avtal/:token" element={<AcceptAgreement />} />
        <Route path="/projekt/:token" element={<ProjectPortal />} />

        {/* Admin routes */}
        <Route path="/admin/*" element={
          <AuthProvider>
            <AdminLanguageProvider>
            <Routes>
              <Route path="login" element={<LoginPage />} />
              <Route path="accept-invite/:token" element={<AcceptInvite />} />
              {/* Accountant-specific route - must be before AdminLayout */}
              <Route path="accountant/*" element={<AccountantLayout />}>
                <Route path="bookkeeping" element={<BookkeepingPage />} />
                <Route index element={<Navigate to="bookkeeping" replace />} />
              </Route>
              {/* Admin routes */}
              <Route element={<AdminLayout />}>
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="leads" element={<LeadsPage />} />
                <Route path="customers" element={<CustomersPage />} />
                <Route path="customers/:id" element={<CustomerDetail />} />
                <Route path="projects" element={<ProjectsPage />} />
                <Route path="invoices" element={<InvoicesPage />} />
                <Route path="articles" element={<ArticlesPage />} />
                <Route path="agreements" element={<AgreementsPage />} />
                <Route path="support" element={<TicketsPage />} />
                <Route path="onboarding" element={<OnboardingSessionsPage />} />
                <Route path="onboarding/:id" element={<OnboardingDetailPage />} />
                <Route path="bookkeeping" element={<BookkeepingPage />} />
                <Route path="outreach" element={<OutreachPage />} />
                <Route path="prompts" element={<PromptsPage />} />
                <Route index element={<Navigate to="dashboard" replace />} />
              </Route>
            </Routes>
            </AdminLanguageProvider>
          </AuthProvider>
        } />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
